/* Signal-replay backtest UI with equity curve chart + A/B compare mode.

   Users pick a channel + date range, optionally set a starting balance and
   override risk params, hit "Run". Backend simulates every historical entry
   signal in the window against real exchange klines and returns an equity
   curve + summary — a real compounding ending balance when starting_balance
   is set. Compare mode runs 2 risk configs against the same signal set — the
   chart overlays both curves so you can see "what if I'd set BE=60%?".
*/
import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import { backtest as backtestApi, channels as channelsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge, cn } from "@/components/atoms";
import { Play, ChartLine, ArrowsHorizontal } from "@phosphor-icons/react";

const VARIANT_COLORS = ["#4F46E5", "#EA580C", "#059669", "#7C3AED", "#DC2626"];

/* ---- Predefined strategy presets — mirrors the "Apply ... Strategy"
   buttons in the Channel edit dialog exactly, so a backtest config matches
   what the channel would actually run live. */
const STRATEGY_PRESETS = {
  trade_talks: {
    name: "Trade Talks Strategy",
    fields: {
      position_sizing_mode: "margin", margin_percent_per_trade: 20,
      follow_provider_leverage: false, default_leverage: 20, max_leverage: 20,
      ignore_provider_tp: true, auto_close_roi_pct: 75,
      use_implied_tp_zone_when_no_explicit_tp: true,
      sl_to_breakeven_at_roi_pct: 0, sl_to_breakeven_offset_pct: 0,
      close_full_at_tp1: true, skip_if_sl_beyond_liquidation: true,
      execute_without_provider_sl: true, layered_entry_enabled: false,
      tp_waterfall_enabled: false, skip_if_tp1_already_hit: false,
      entry_sl_band_gate_enabled: true, entry_sl_band_buffer_pct: 0.3,
    },
  },
  trade_talks_v2: {
    name: "Trade Talks Strategy V2",
    fields: {
      position_sizing_mode: "risk", risk_percent_per_trade: 15,
      follow_provider_leverage: false, default_leverage: 20, max_leverage: 20,
      ignore_provider_tp: true, auto_close_roi_pct: 30,
      use_implied_tp_zone_when_no_explicit_tp: true,
      sl_to_breakeven_at_roi_pct: 30, sl_to_breakeven_offset_pct: 2,
      close_full_at_tp1: false, skip_if_sl_beyond_liquidation: true,
      auto_delever_to_fit_sl: true, execute_without_provider_sl: true,
      layered_entry_enabled: false, tp_waterfall_enabled: false,
      skip_if_tp1_already_hit: false, entry_sl_band_gate_enabled: true,
      entry_sl_band_buffer_pct: 0.3,
    },
  },
  trade_talks_v3: {
    name: "Trade Talks Strategy V3",
    fields: {
      position_sizing_mode: "risk", risk_percent_per_trade: 20,
      follow_provider_leverage: false, default_leverage: 25, max_leverage: 25,
      auto_delever_to_fit_sl: true, auto_close_roi_pct: 50,
      ignore_provider_tp_always: true, close_full_at_tp1: true,
      skip_if_sl_beyond_liquidation: false, execute_without_provider_sl: true,
      layered_entry_enabled: false, tp_waterfall_enabled: false,
      skip_if_tp1_already_hit: false, entry_sl_band_gate_enabled: false,
      sl_to_breakeven_at_roi_pct: 0, sl_to_breakeven_offset_pct: 0,
      ignore_provider_tp: false, use_implied_tp_zone_when_no_explicit_tp: false,
    },
  },
  trade_sensei: {
    name: "Trade Sensei Strategy",
    fields: {
      position_sizing_mode: "margin", margin_percent_per_trade: 30,
      follow_provider_leverage: false, default_leverage: 10, max_leverage: 10,
      auto_close_roi_pct: 0, sl_to_breakeven_at_roi_pct: 0,
      sl_to_breakeven_offset_pct: 0, close_full_at_tp1: false,
      skip_if_sl_beyond_liquidation: true, layered_entry_enabled: true,
      layered_entry_pct1: 70, tp_waterfall_enabled: true, tp1_close_pct: 50,
      tp2_close_pct: 30, sl_trail_to_be_at_tp1: true, sl_trail_to_tp1_at_tp2: true,
      skip_if_tp1_already_hit: true, ignore_provider_tp: false,
      use_implied_tp_zone_when_no_explicit_tp: false,
      entry_sl_band_gate_enabled: false,
    },
  },
  trade_sensei_v2: {
    name: "Trade Sensei Strategy V2",
    fields: {
      position_sizing_mode: "margin", margin_percent_per_trade: 30,
      follow_provider_leverage: false, default_leverage: 12, max_leverage: 12,
      auto_close_roi_pct: 0, sl_to_breakeven_at_roi_pct: 50,
      sl_to_breakeven_offset_pct: 0, close_full_at_tp1: false,
      skip_if_sl_beyond_liquidation: true, auto_delever_to_fit_sl: true,
      layered_entry_enabled: true, layered_entry_pct1: 70, tp_waterfall_enabled: true,
      tp1_close_pct: 50, tp2_close_pct: 30, sl_trail_to_be_at_tp1: true,
      sl_trail_to_tp1_at_tp2: true, skip_if_tp1_already_hit: true,
      ignore_provider_tp: false, use_implied_tp_zone_when_no_explicit_tp: false,
      entry_sl_band_gate_enabled: false,
    },
  },
  trade_sensei_v3: {
    name: "Trade Sensei Strategy V3",
    fields: {
      position_sizing_mode: "margin", margin_percent_per_trade: 30,
      follow_provider_leverage: false, default_leverage: 12, max_leverage: 12,
      sl_to_breakeven_at_roi_pct: 50, sl_to_breakeven_offset_pct: 0,
      close_full_at_tp1: false, skip_if_sl_beyond_liquidation: true,
      auto_delever_to_fit_sl: true, execute_without_provider_sl: true,
      layered_entry_enabled: true, layered_entry_pct1: 70, tp_waterfall_enabled: true,
      tp1_close_pct: 50, tp2_close_pct: 30, sl_trail_to_be_at_tp1: true,
      sl_trail_to_tp1_at_tp2: true, skip_if_tp1_already_hit: true,
      ignore_provider_tp: false, use_implied_tp_zone_when_no_explicit_tp: false,
      entry_sl_band_gate_enabled: false,
      ignore_provider_tp_always: true, auto_close_roi_pct: 100,
      low_leverage_roi_threshold: 6, low_leverage_roi_pct: 50,
    },
  },
};

/* ---- Helpers ---- */
function _fmtDate(v) {
  if (!v) return "—";
  const d = typeof v === "number" ? new Date(v) : new Date(v);
  return isNaN(d) ? "—" : d.toISOString().slice(0, 16).replace("T", " ");
}
function _pnlTone(v) {
  if (typeof v !== "number") return "";
  return v > 0 ? "text-emerald-600" : v < 0 ? "text-red-600" : "";
}

/* ---- Reusable risk-override editor ---- */
function RiskOverrideEditor({ overrides, onChange, label, title }) {
  const upd = (k, v) => onChange({ ...overrides, [k]: v, _preset: undefined });
  const sizingMode = overrides.position_sizing_mode || "risk";
  const applyPreset = (key) => {
    if (!key || key === "custom") return;
    const preset = STRATEGY_PRESETS[key];
    if (!preset) return;
    // Full replace, not merge — a field this preset doesn't mention (e.g.
    // entry_sl_band_gate_enabled, only set by other presets) must NOT
    // survive from whatever was configured before, or switching presets
    // silently contaminates the "clean" config with leftover settings.
    onChange({ _label: overrides._label, ...preset.fields, _preset: key });
  };
  return (
    <div className="space-y-3 p-4 rounded-md border border-slate-200 bg-slate-50/50">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">
          {title || label}
        </p>
        <Select value={overrides._preset || "custom"} onValueChange={applyPreset}>
          <SelectTrigger data-testid={`bt-override-preset-${label}`}
                         className="h-7 text-[11px] w-auto min-w-[150px] bg-white">
            <SelectValue placeholder="Load a strategy…" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">Custom / manual</SelectItem>
            {Object.entries(STRATEGY_PRESETS).map(([key, p]) => (
              <SelectItem key={key} value={key} data-testid={`bt-override-preset-${label}-${key}`}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
          How much to bet per trade
        </p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] text-slate-500 uppercase">Sizing method</Label>
            <Select value={sizingMode}
                    onValueChange={(v) => upd("position_sizing_mode", v)}>
              <SelectTrigger data-testid={`bt-override-sizing-mode-${label}`} className="h-9 text-sm bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="risk">Risk % — lose this if SL hits</SelectItem>
                <SelectItem value="margin">Margin % — put up as collateral</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {sizingMode === "margin" ? (
            <_NumField label="Margin % / trade" value={overrides.margin_percent_per_trade}
                       onChange={(v) => upd("margin_percent_per_trade", v)}
                       testId={`bt-override-margin-pct-${label}`} step="0.5"
                       hint="% of balance committed as collateral" />
          ) : (
            <_NumField label="Risk % / trade" value={overrides.risk_percent_per_trade}
                       onChange={(v) => upd("risk_percent_per_trade", v)}
                       testId={`bt-override-risk-pct-${label}`} step="0.1"
                       hint="% of balance lost if the stop-loss hits" />
          )}
          <_NumField label="Leverage" value={overrides.default_leverage}
                     onChange={(v) => upd("default_leverage", v)}
                     testId={`bt-override-leverage-${label}`}
                     hint="Used when the signal doesn't say" />
          <_NumField label="Max leverage" value={overrides.max_leverage}
                     onChange={(v) => upd("max_leverage", v)}
                     testId={`bt-override-lev-${label}`}
                     hint="Hard cap, even if a signal asks for more" />
        </div>
      </div>

      <div>
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5 pt-2 border-t border-slate-200">
          When to exit early
        </p>
        <div className="grid grid-cols-2 gap-2">
          <_NumField label="Move stop to breakeven at ROI %" value={overrides.sl_to_breakeven_at_roi_pct}
                     onChange={(v) => upd("sl_to_breakeven_at_roi_pct", v)}
                     testId={`bt-override-be-${label}`} hint="0 = never move it" />
          <_NumField label="Breakeven offset %" value={overrides.sl_to_breakeven_offset_pct}
                     onChange={(v) => upd("sl_to_breakeven_offset_pct", v)}
                     testId={`bt-override-be-offset-${label}`} step="0.05"
                     hint="How far past entry to lock in" />
          <_NumField label="Auto-close at ROI %" value={overrides.auto_close_roi_pct}
                     onChange={(v) => upd("auto_close_roi_pct", v)}
                     testId={`bt-override-ac-${label}`} hint="Force-close once profit hits this — 0 = off" />
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
        <div>
          <p className="text-xs text-slate-700 font-medium">Close 100% at first take-profit</p>
          <p className="text-[10px] text-slate-500">Off = bank part of it and let the rest ride</p>
        </div>
        <Switch checked={overrides.close_full_at_tp1 !== false}
                onCheckedChange={(v) => upd("close_full_at_tp1", v)}
                data-testid={`bt-override-tp1-${label}`} />
      </div>

      <details className="pt-2 border-t border-slate-200" data-testid={`bt-override-advanced-${label}`}>
        <summary className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-600">
          Advanced — entry filters, TP handling, deleverage
        </summary>
        <div className="mt-3 space-y-3">
          <_BoolField label="Don't chase (skip if TP1 already hit)"
                      hint="Skip the entry outright if price already ran past TP1 before we'd process it"
                      checked={!!overrides.skip_if_tp1_already_hit}
                      onChange={(v) => upd("skip_if_tp1_already_hit", v)}
                      testId={`bt-override-dont-chase-${label}`} />
          <_BoolField label="Entry-SL band gate"
                      hint="For a real provider SL: enter anywhere between entry and SL (±buffer) instead of the exact tick, skip if mark already passed SL"
                      checked={!!overrides.entry_sl_band_gate_enabled}
                      onChange={(v) => upd("entry_sl_band_gate_enabled", v)}
                      testId={`bt-override-sl-band-${label}`} />
          {overrides.entry_sl_band_gate_enabled && (
            <_NumField label="Entry-SL band buffer %" value={overrides.entry_sl_band_buffer_pct}
                       onChange={(v) => upd("entry_sl_band_buffer_pct", v)}
                       testId={`bt-override-sl-band-buffer-${label}`} step="0.1"
                       hint="Near-miss allowance past the stated entry" />
          )}
          <_BoolField label="Ignore provider TP when none is real"
                      hint="When a signal has no genuine TP, fall back to a synthetic ROI-based target instead of riding open-ended (a real provider TP always wins regardless)"
                      checked={!!overrides.ignore_provider_tp}
                      onChange={(v) => upd("ignore_provider_tp", v)}
                      testId={`bt-override-ignore-tp-${label}`} />
          <_BoolField label="Use chart's implied TP zone when no explicit TP"
                      hint="Prefer a chart-derived profit zone over the flat ROI target when the signal has no stated TP"
                      checked={!!overrides.use_implied_tp_zone_when_no_explicit_tp}
                      onChange={(v) => upd("use_implied_tp_zone_when_no_explicit_tp", v)}
                      testId={`bt-override-implied-zone-${label}`} />
          <_BoolField label="Always ignore provider TP (force ROI target)"
                      hint="Unlike the flag above, this bypasses the provider's stated TP entirely, even when a real one exists — every trade closes at the flat ROI target instead"
                      checked={!!overrides.ignore_provider_tp_always}
                      onChange={(v) => upd("ignore_provider_tp_always", v)}
                      testId={`bt-override-ignore-tp-always-${label}`} />
          <_BoolField label="Layered entry (EP1/EP2)"
                      hint="Split size across two entry prices when the signal gives both"
                      checked={!!overrides.layered_entry_enabled}
                      onChange={(v) => upd("layered_entry_enabled", v)}
                      testId={`bt-override-layered-${label}`} />
          {overrides.layered_entry_enabled && (
            <_NumField label="EP1 size %" value={overrides.layered_entry_pct1}
                       onChange={(v) => upd("layered_entry_pct1", v)}
                       testId={`bt-override-layered-pct1-${label}`} step="1"
                       hint="Rest goes to EP2 if/when it fills" />
          )}
          <_BoolField label="Auto-delever to fit SL"
                      hint="If the SL is wider than leverage can survive, lower leverage for that trade instead of skipping it"
                      checked={!!overrides.auto_delever_to_fit_sl}
                      onChange={(v) => upd("auto_delever_to_fit_sl", v)}
                      testId={`bt-override-delever-${label}`} />
          {overrides.auto_delever_to_fit_sl && (
            <div className="grid grid-cols-2 gap-2">
              <_NumField label="Low-leverage ROI cutoff (x)" value={overrides.low_leverage_roi_threshold}
                         onChange={(v) => upd("low_leverage_roi_threshold", v)}
                         testId={`bt-override-lowlev-threshold-${label}`} step="1"
                         hint="Trades deleveraged to this leverage or below use the lower target instead — 0/blank = off" />
              <_NumField label="Low-leverage ROI target %" value={overrides.low_leverage_roi_pct}
                         onChange={(v) => upd("low_leverage_roi_pct", v)}
                         testId={`bt-override-lowlev-roi-${label}`} step="1"
                         hint="A wide SL that forced deleveraging needs a bigger move for the same ROI%" />
            </div>
          )}
          <_BoolField label="TP waterfall (TP1/TP2/TP3 partial closes)"
                      hint="Close a % of the position at each TP level instead of one all-or-nothing exit"
                      checked={!!overrides.tp_waterfall_enabled}
                      onChange={(v) => upd("tp_waterfall_enabled", v)}
                      testId={`bt-override-waterfall-${label}`} />
          {overrides.tp_waterfall_enabled && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <_NumField label="Close % at TP1" value={overrides.tp1_close_pct}
                           onChange={(v) => upd("tp1_close_pct", v)}
                           testId={`bt-override-tp1-pct-${label}`} step="1" />
                <_NumField label="Close % at TP2" value={overrides.tp2_close_pct}
                           onChange={(v) => upd("tp2_close_pct", v)}
                           testId={`bt-override-tp2-pct-${label}`} step="1"
                           hint="Last level takes whatever remains" />
              </div>
              <_BoolField label="Trail stop to breakeven at TP1"
                          checked={!!overrides.sl_trail_to_be_at_tp1}
                          onChange={(v) => upd("sl_trail_to_be_at_tp1", v)}
                          testId={`bt-override-trail-be-${label}`} />
              <_BoolField label="Trail stop to TP1's price at TP2"
                          checked={!!overrides.sl_trail_to_tp1_at_tp2}
                          onChange={(v) => upd("sl_trail_to_tp1_at_tp2", v)}
                          testId={`bt-override-trail-tp1-${label}`} />
            </>
          )}
        </div>
      </details>
    </div>
  );
}

function _NumField({ label, value, onChange, testId, step = "1", hint }) {
  return (
    <div>
      <Label className="text-[10px] text-slate-500 uppercase">{label}</Label>
      <Input data-testid={testId} type="number" step={step}
             value={value ?? ""}
             onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
             className="h-9 mono text-sm bg-white" placeholder="—" />
      {hint && <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{hint}</p>}
    </div>
  );
}

function _BoolField({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <p className="text-xs text-slate-700 font-medium">{label}</p>
        {hint && <p className="text-[10px] text-slate-500 leading-tight">{hint}</p>}
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}

/* ---- Main section ---- */
export default function BacktestSection() {
  const chQ = useQuery({
    queryKey: ["channels"],
    queryFn: async () => (await channelsApi.list()).data,
  });
  const chs = chQ.data || [];

  const [filters, setFilters] = useState({
    channel_id: "",   // "" = all channels
    from_date: "",
    to_date: "",
    symbol: "",
    starting_balance: "",   // "" = fixed-$1000 reference mode (no real compounding)
  });
  const [mode, setMode] = useState("single");   // "single" | "compare"
  const [overrideA, setOverrideA] = useState({ _label: "A · current config",
                                                position_sizing_mode: "risk" });
  const [overrideB, setOverrideB] = useState({ _label: "B · with BE=60%",
                                                position_sizing_mode: "risk",
                                                sl_to_breakeven_at_roi_pct: 60 });

  const [result, setResult] = useState(null);

  const runSingle = useMutation({
    mutationFn: () => backtestApi.run({
      channel_id: filters.channel_id || null,
      from_date: filters.from_date || null,
      to_date: filters.to_date || null,
      symbol: filters.symbol || null,
      starting_balance: filters.starting_balance === "" ? null : Number(filters.starting_balance),
      risk_override: _pruneOverride(overrideA),
    }),
    onSuccess: (r) => {
      setResult({ mode: "single", ...r.data });
      toast.success(`Simulated ${r.data.summary.simulated_trades} trade(s)`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Backtest failed"),
  });

  const runCompare = useMutation({
    mutationFn: () => backtestApi.compare({
      channel_id: filters.channel_id || null,
      from_date: filters.from_date || null,
      to_date: filters.to_date || null,
      symbol: filters.symbol || null,
      starting_balance: filters.starting_balance === "" ? null : Number(filters.starting_balance),
      configs: [_pruneOverride(overrideA), _pruneOverride(overrideB)]
        .map((c, i) => ({ _label: [overrideA, overrideB][i]._label, ...c })),
    }),
    onSuccess: (r) => {
      setResult({ mode: "compare", ...r.data });
      toast.success(`Compared ${r.data.variants.length} configs`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Compare failed"),
  });

  const running = runSingle.isPending || runCompare.isPending;

  return (
    <div className="space-y-4" data-testid="section-backtest">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ChartLine size={22} color="#0891B2" weight="fill" />
            <div>
              <h3 className="font-display text-xl">Signal Replay & Backtest</h3>
              <p className="text-xs text-slate-500 max-w-xl">
                See what a channel's real past signals would have done to your account —
                for free, with no money on the line. Pick a channel and a starting
                balance below, hit run, and you'll get a real ending balance plus a
                trade-by-trade breakdown.
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">Compare two configs</span>
              <Switch data-testid="bt-compare-mode-toggle"
                      checked={mode === "compare"}
                      onCheckedChange={(v) => setMode(v ? "compare" : "single")} />
            </div>
            <p className="text-[10px] text-slate-400">Side-by-side, e.g. "what if I used less leverage?"</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="overline">Channel</Label>
            <Select value={filters.channel_id || "all"}
                    onValueChange={(v) => setFilters({ ...filters, channel_id: v === "all" ? "" : v })}>
              <SelectTrigger data-testid="bt-channel-picker" className="h-10">
                <SelectValue placeholder="All channels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels</SelectItem>
                {chs.map((c) => (
                  <SelectItem key={c.id} value={c.id}
                              data-testid={`bt-channel-${c.id}`}>
                    {c.name} <span className="text-slate-400 text-xs">· {c.exchange}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="overline">From (UTC)</Label>
            <Input data-testid="bt-from-date" type="datetime-local"
                   value={filters.from_date}
                   onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                   className="h-10 mono" />
          </div>
          <div>
            <Label className="overline">To (UTC)</Label>
            <Input data-testid="bt-to-date" type="datetime-local"
                   value={filters.to_date}
                   onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                   className="h-10 mono" />
          </div>
          <div>
            <Label className="overline">Symbol (optional)</Label>
            <Input data-testid="bt-symbol" placeholder="e.g. BTCUSDT"
                   value={filters.symbol}
                   onChange={(e) => setFilters({ ...filters, symbol: e.target.value.toUpperCase() })}
                   className="h-10 mono" />
          </div>
          <div>
            <Label className="overline">Starting balance ($)</Label>
            <Input data-testid="bt-starting-balance" type="number" step="10"
                   placeholder="optional"
                   value={filters.starting_balance}
                   onChange={(e) => setFilters({ ...filters, starting_balance: e.target.value })}
                   className="h-10 mono" />
            <div className="flex items-center gap-1 mt-1">
              {[100, 200, 500, 1000].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  data-testid={`bt-balance-preset-${amt}`}
                  onClick={() => setFilters({ ...filters, starting_balance: String(amt) })}
                  className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                >
                  ${amt}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className="text-[10px] text-slate-400 -mt-2 mb-2">
          Leave the starting balance blank to just compare risk configs by win rate/drawdown.
          Set one to see a real projected ending balance.
        </p>

        <div className={cn("mt-4 grid gap-3",
                           mode === "compare" ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1")}>
          <RiskOverrideEditor overrides={overrideA} onChange={setOverrideA}
                              label={mode === "compare" ? "A" : "risk"}
                              title={mode === "compare" ? "Config A" : "Risk & sizing settings"} />
          {mode === "compare" && (
            <RiskOverrideEditor overrides={overrideB} onChange={setOverrideB}
                                label="B" title="Config B" />
          )}
        </div>

        <div className="mt-4 flex items-center gap-3">
          <Button
            data-testid="bt-run-btn"
            onClick={() => (mode === "compare" ? runCompare.mutate() : runSingle.mutate())}
            disabled={running}
          >
            {running ? "Running…" : (
              <>
                {mode === "compare"
                  ? <ArrowsHorizontal size={16} className="mr-2" />
                  : <Play size={16} className="mr-2" weight="fill" />}
                {mode === "compare" ? "Compare A vs B" : "Run backtest"}
              </>
            )}
          </Button>
          {running && (
            <p className="text-xs text-slate-500" data-testid="bt-running-note">
              Fetching real price history for every signal — wide date ranges or chatty
              channels can take a minute or two.
            </p>
          )}
        </div>
      </div>

      {result ? <ResultPanel result={result} /> : (
        <div className="card-surface p-6 text-center" data-testid="bt-empty-state">
          <p className="text-sm text-slate-500">
            Pick a channel above (or leave it as "All channels"), optionally set a
            starting balance, and hit <span className="font-medium text-slate-700">Run backtest</span>.
            You'll get win rate, drawdown, and — if you set a balance — a real
            projected ending balance for that exact stretch of signals.
          </p>
        </div>
      )}
    </div>
  );
}

function _pruneOverride(o) {
  // Strip empty values so the backend doesn't override with `undefined`/NaN.
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    if (k === "_label" || k === "_preset") continue;
    if (v === undefined || v === "" || Number.isNaN(v)) continue;
    out[k] = v;
  }
  return out;
}

function ResultPanel({ result }) {
  const variants = result.mode === "compare"
    ? result.variants
    : [{ label: "Backtest", ...result }];

  const chartData = useMemo(() => {
    // Flatten each variant's equity_curve into a joined timestamp series.
    // If timestamps overlap, we key by (variant + ts) so lines can coexist.
    const merged = new Map();
    variants.forEach((v, i) => {
      (v.equity_curve || []).forEach((pt) => {
        const key = pt.ts;
        if (!merged.has(key)) merged.set(key, { ts: pt.ts });
        merged.get(key)[`v${i}`] = pt.equity;
      });
    });
    return Array.from(merged.values()).sort((a, b) => a.ts - b.ts);
  }, [variants]);

  const hasProjection = variants.some((v) => v.summary.starting_balance != null);

  return (
    <div className="card-surface p-6" data-testid="bt-results">
      <p className="overline mb-3">Results</p>

      {/* --- Per-config summary card. One card per variant either way — when
          a starting balance is set, it's the real $ balance + stats in one
          place instead of a separate "balance projection" card and a
          fixed-$1000-reference "summary" card that can tell different
          stories for the same config (the old layout showed 4 boxes in
          compare mode and users reasonably asked why two of them disagreed
          — they're different bases, not a bug, but showing both invites
          exactly that confusion). --- */}
      {hasProjection ? (
        <div className={cn("grid gap-3 mb-4",
                            variants.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1")}>
          {variants.map((v, i) => {
            const s = v.summary;
            const positive = s.ending_balance >= s.starting_balance;
            return (
              <div key={i} className="rounded-md border border-slate-200 p-3 bg-slate-50/50"
                   data-testid={`bt-projection-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full"
                          style={{ background: VARIANT_COLORS[i % VARIANT_COLORS.length] }} />
                    <p className="text-sm font-semibold">{v.label}</p>
                  </div>
                  {s.wiped_out && (
                    <Badge variant="loss" data-testid={`bt-wiped-out-${i}`}>Wiped out</Badge>
                  )}
                </div>
                {/* Server-resolved sizing actually used for this run — shown
                    explicitly so it's never ambiguous which config produced
                    which number, independent of what the form currently shows. */}
                <p className="text-[10px] text-slate-400 mb-2" data-testid={`bt-effective-sizing-${i}`}>
                  {v.effective_risk?.position_sizing_mode === "margin"
                    ? `Margin mode · ${v.effective_risk?.margin_percent_per_trade ?? "?"}% margin/trade`
                    : `Risk mode · ${v.effective_risk?.risk_percent_per_trade ?? "?"}% risk/trade`}
                  {" · "}{v.effective_risk?.default_leverage ?? "?"}x leverage
                </p>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="mono text-sm text-slate-500">${s.starting_balance.toFixed(2)}</span>
                  <span className="text-slate-400">→</span>
                  <span className={cn("mono text-lg font-semibold", positive ? "text-emerald-600" : "text-red-600")}>
                    ${s.ending_balance.toFixed(2)}
                  </span>
                  <span className={cn("mono text-sm font-medium", positive ? "text-emerald-600" : "text-red-600")}>
                    ({s.total_return_pct > 0 ? "+" : ""}{s.total_return_pct}%)
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-slate-500">Trades</p>
                    <p className="mono font-semibold text-sm">{s.simulated_trades}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Win rate</p>
                    <p className="mono font-semibold text-sm">{s.win_rate_pct}%</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Wins/Losses</p>
                    <p className="mono text-sm">{s.wins}/{s.losses}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Max drawdown</p>
                    <p className="mono text-sm text-red-600">-{s.max_drawdown_pct}%</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Min balance seen</p>
                    <p className="mono text-sm">${s.min_balance_seen.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Max balance seen</p>
                    <p className="mono text-sm">${s.max_balance_seen.toFixed(2)}</p>
                  </div>
                </div>
                {s.total_return_pct > 500 && (
                  <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                    Reinvesting a fixed % of a fast-growing balance compounds quickly on paper —
                    real trading has slippage and liquidity limits this projection doesn't model.
                    Read this as directional, not a promise.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={cn("grid gap-3 mb-4",
                            variants.length > 1 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2 md:grid-cols-4")}>
          {variants.map((v, i) => (
            <div key={i} className="rounded-md border border-slate-200 p-3"
                 data-testid={`bt-summary-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-3 h-3 rounded-full"
                      style={{ background: VARIANT_COLORS[i % VARIANT_COLORS.length] }} />
                <p className="text-sm font-semibold">{v.label}</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                <div>
                  <p className="text-slate-500">PnL</p>
                  <p className={cn("mono font-semibold text-sm", _pnlTone(v.summary.total_pnl_usdt))}>
                    ${v.summary.total_pnl_usdt.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Trades</p>
                  <p className="mono font-semibold text-sm">{v.summary.simulated_trades}</p>
                </div>
                <div>
                  <p className="text-slate-500">Win rate</p>
                  <p className="mono font-semibold text-sm">
                    {v.summary.win_rate_pct}%
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Wins/Losses</p>
                  <p className="mono text-sm">{v.summary.wins}/{v.summary.losses}</p>
                </div>
                <div>
                  <p className="text-slate-500">Max DD</p>
                  <p className="mono text-sm text-red-600">
                    -${v.summary.max_drawdown_usdt.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500">Signals</p>
                  <p className="mono text-sm text-slate-500">
                    {v.summary.total_signals_matched}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* --- Balance ledger: date, margin used, $ P&L, running balance per
          trade — one per variant, not just the first, so a compare run's
          two configs can each be inspected (they can diverge a lot, as
          A/B here does, and the whole point of comparing is seeing why). --- */}
      {hasProjection && variants.map((v, vi) => {
        const ledgerRows = (v.trades || []).filter((t) => t.balance_after != null);
        if (ledgerRows.length === 0) return null;
        return (
          <div key={vi} className="mb-4" data-testid={`bt-ledger-${vi}`}>
            <p className="text-sm font-semibold mb-2 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full"
                    style={{ background: VARIANT_COLORS[vi % VARIANT_COLORS.length] }} />
              Balance ledger — {v.label}
            </p>
            <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-md border border-slate-200">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500 sticky top-0 bg-white border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1.5 px-2">Opened</th>
                    <th className="text-left py-1.5 px-2">Closed</th>
                    <th className="text-left py-1.5 px-2">Symbol</th>
                    <th className="text-left py-1.5 px-2">Side</th>
                    <th className="text-right py-1.5 px-2">Margin used</th>
                    <th className="text-right py-1.5 px-2">P&amp;L ($)</th>
                    <th className="text-right py-1.5 px-2">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerRows.map((t, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5 px-2 mono text-slate-500">{_fmtDate(t.entry_ts)}</td>
                      <td className="py-1.5 px-2 mono text-slate-600">{_fmtDate(t.exit_ts)}</td>
                      <td className="py-1.5 px-2 mono">{t.symbol}</td>
                      <td className="py-1.5 px-2">
                        <Badge variant={t.side === "long" ? "profit" : "loss"}>{t.side}</Badge>
                      </td>
                      <td className="py-1.5 px-2 mono text-right">${t.margin_used.toFixed(2)}</td>
                      <td className={cn("py-1.5 px-2 mono text-right font-semibold", _pnlTone(t.dollar_pnl))}>
                        {t.dollar_pnl > 0 ? "+" : ""}${t.dollar_pnl.toFixed(2)}
                      </td>
                      <td className="py-1.5 px-2 mono text-right font-semibold">${t.balance_after.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* --- Equity chart --- */}
      {chartData.length > 0 ? (
        <div className="h-72 mt-2" data-testid="bt-equity-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="ts" tickFormatter={_fmtDate}
                     tick={{ fill: "#64748b", fontSize: 10 }}
                     stroke="#94a3b8" />
              <YAxis tick={{ fill: "#64748b", fontSize: 10 }}
                     tickFormatter={(v) => `$${v}`} stroke="#94a3b8" />
              <Tooltip
                labelFormatter={_fmtDate}
                contentStyle={{
                  background: "#0f172a",
                  border: "none",
                  color: "#f8fafc",
                  fontSize: "11px",
                }}
                formatter={(v) => (typeof v === "number" ? `$${v.toFixed(2)}` : v)}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
              {variants.map((v, i) => (
                <Line key={i} type="monotone" dataKey={`v${i}`} name={v.label}
                      stroke={VARIANT_COLORS[i % VARIANT_COLORS.length]}
                      strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-sm text-slate-500 py-6 text-center"
           data-testid="bt-no-trades">
          No trades simulated. Widen the date range, pick a different
          channel, or verify the channel has entry signals in this window.
        </p>
      )}

      {/* --- Trade breakdown — one per variant --- */}
      {variants.map((v, vi) => (
        (v.trades?.length > 0) && (
          <details key={vi} className="mt-4" data-testid={`bt-trade-list-toggle-${vi}`}>
            <summary className="text-xs text-slate-600 cursor-pointer hover:text-slate-900 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full"
                    style={{ background: VARIANT_COLORS[vi % VARIANT_COLORS.length] }} />
              Trade-by-trade breakdown — {v.label} ({v.trades.length} rows)
            </summary>
            <div className="mt-2 overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase text-slate-500 sticky top-0 bg-white border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1.5 px-2">Symbol</th>
                    <th className="text-left py-1.5 px-2">Side</th>
                    <th className="text-right py-1.5 px-2">Entry</th>
                    <th className="text-right py-1.5 px-2">Exit</th>
                    <th className="text-left py-1.5 px-2">Reason</th>
                    <th className="text-right py-1.5 px-2">Hold min</th>
                    <th className="text-right py-1.5 px-2">PnL %</th>
                    <th className="text-center py-1.5 px-2">BE</th>
                  </tr>
                </thead>
                <tbody>
                  {v.trades.map((t, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-1.5 px-2 mono">{t.symbol}</td>
                      <td className="py-1.5 px-2">
                        <Badge variant={t.side === "long" ? "profit" : "loss"}>
                          {t.side}
                        </Badge>
                      </td>
                      <td className="py-1.5 px-2 mono text-right">{t.entry_price}</td>
                      <td className="py-1.5 px-2 mono text-right">{t.exit_price}</td>
                      <td className="py-1.5 px-2 text-slate-600">{t.exit_reason}</td>
                      <td className="py-1.5 px-2 mono text-right">{t.holding_minutes}</td>
                      <td className={cn("py-1.5 px-2 mono text-right font-semibold",
                                         _pnlTone(t.pnl_pct_margin))}>
                        {t.pnl_pct_margin > 0 ? "+" : ""}{t.pnl_pct_margin}%
                      </td>
                      <td className="py-1.5 px-2 text-center">{t.sl_moved_to_be ? "✓" : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )
      ))}
    </div>
  );
}
